// NPM
import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import moment from "moment";
import { isEmpty, isNull, omitBy, omit } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { CreateEditVersionSchema } from './create-edit-validation';

// STYLE
import '../modal-forms/modal-forms.scss';

// ANT COMPONENTS
import { Col, Row, Checkbox } from "antd";
import ModuleTitleBar from 'Components/shared/module-title-bar/module-title-bar';
import { 
    AlertBox, 
    Button, 
    DateRange, 
    TextInput 
} from "@life-line-lab/components-react";

interface CreateEditProps {
    tab: any; // differentiate tab
    type: any; // of modal not tab
    info: any; // the version data
    onCancel: any;
    onUpdate?: any;
    onSubmit?: any;
}

export const CreateEditForm: React.FC<CreateEditProps> = ({
    tab, 
    type,
    info,
    onCancel,
    onUpdate,
    onSubmit,
}) => {
    const { t } = useTranslation();

    const initialInput = {
        maxWeight: "",
        length: "",
        width: "",
        height: "",
        isFixedAmount: false,
        fixedAmount: "",
        isPercentage: false,
        percentage: "",
        capAmount: "",
        validFrom: {
            date: null,
            dateString: ""
        },
        validTo: {
            date: null,
            dateString: ""
        }
    };
    const initialError = {};

    const [input, setInput] = useState(initialInput);
    const [error, setError] = useState<any>(initialError);

    useEffect(() => {
        type == "edit"
            ? setInput({
                ...input, ...info,
                code: "",
                settingID: info._id,
                isFixedAmount: info.data ? info.isFixedAmount : !!info.surcharge.fixedAmount,
                isPercentage: info.data ? info.isPercentage : !!info.surcharge.percentage,
                fixedAmount: info.data ? info.fixedAmount : info.surcharge.fixedAmount,
                percentage: info.data ? info.percentage : info.surcharge.percentage,
                capAmount: info.data ? info.capAmount : info.surcharge.capAmount,
                validFrom: {
                    date: info.data ? info.validFrom : moment(info.validFrom),
                    dateString: moment(info.validFrom).toISOString()
                },
                validTo: {
                    date: info.data ? info.validTo : moment(info.validTo),
                    dateString: moment(info.validTo).toISOString()
                }
            })
            : setInput({ ...info, ...input });
    }, [info]);

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(initialError);
        setInput({ ...input, [e.target.name]: e.target.value });
    };

    const handleCheckboxChange = (checked: boolean, name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: checked });
    };
    
    const handleDateChange = (startDate: any, endDate: any) => {
        if (startDate.date) {
            setError(initialError);
            setInput({ ...input, validFrom: startDate });
        } else if (endDate.date) {
            setError(initialError);
            setInput({ ...input, validTo: endDate });
        }
    };

    const handleSubmit = () => {
        try {
            const valInput = omitBy({
                ...input,
                tab: tab,
                serviceTypes: info?.serviceTypes?.map((e) => e.code) || "",
                deliveryTypes: info?.deliveryTypes?.map((e) => e.code) || "",
                surcharge: omitBy({ 
                    // selected but empty attr is unvalidated if pair is valid
                    fixedAmount: input.isFixedAmount ? input.fixedAmount : "",
                    percentage: input.isPercentage ? input.percentage : "",
                    capAmount: input.capAmount
                }, isEmpty),
                maxDimension: omitBy({
                    length: input.length,
                    width: input.width,
                    height: input.height,
                  }, isEmpty),
                validFrom: moment(input?.validFrom?.date).toISOString(),
                validTo: moment(input?.validTo?.date).toISOString(),
            }, isEmpty);
            const value = CreateEditVersionSchema.validateSync(omitBy(valInput, isNull), {
                strict: false,
                abortEarly: true,
                stripUnknown: true,
            });
            type == "edit" ? onUpdate(omit(value, ["tab"])) : onSubmit(omit(value, ["tab"]));
        } catch (err) {
            console.log(err);
            const { path, value }: any = errorHandler.validation(err);
            setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
        }
    };

    return (
        <div className="modal-forms--create-edit">
            <ModuleTitleBar
                titleText={type == 'create'
                    ? t("title.createNewVersion").toUpperCase()
                    : t("title.editVersion")}
                haveSubtitle
                subtitleText={type == 'create'
                    ? t("title.version") + " " + (parseInt(info.version) + 1)
                    : t("title.version") + " " + info.version 
                }
            />
            <form className='mb-10'>
                {tab > 1 ? null :
                    <Row>
                        <Col span={24}>
                            <TextInput
                                isRequired={true}
                                label={t("label.maxWeight")}
                                placeholder={t("txt.maxWeight")}
                                value={input.maxWeight}
                                name="maxWeight"
                                onChange={handleFieldChange}
                            />
                            <AlertBox text={error.maxWeight} />
                        </Col>
                        <Col span={24}>
                            <TextInput
                                isRequired={true}
                                label={t("label.maxDimensions")}
                                placeholder={t("txt.length")}
                                value={input.length}
                                name="length"
                                onChange={handleFieldChange}
                            />
                            <AlertBox text={error["maxDimension.length"]} />
                        </Col>
                        <Col span={24}>
                            <TextInput
                                isRequired={true}
                                placeholder={t("txt.width")}
                                value={input.width}
                                name="width"
                                onChange={handleFieldChange}
                            />
                            <AlertBox text={error["maxDimension.width"]} />
                        </Col>
                        <Col span={24}>
                            <TextInput
                                isRequired={true}
                                placeholder={t("txt.height")}
                                value={input.height}
                                name="height"
                                onChange={handleFieldChange}
                            />
                            <AlertBox text={error["maxDimension.height"]} />
                        </Col>
                    </Row>
                }
                <Row>
                    <Col span={24} className="mb-0">
                        <label className='inp-label inp-label--required'>
                            {t('label.surcharge')}
                        </label>
                        <Row gutter={[20, 0]} align='middle'>
                            <Col xs={24} sm={24} md={8} className="mb-9 pr-0">
                                <Checkbox
                                    checked={input.isFixedAmount}
                                    onChange={e => handleCheckboxChange(e.target.checked, "isFixedAmount")}
                                >
                                    {t("label.fixedAmount")}
                                </Checkbox>
                            </Col>
                            <Col xs={24} sm={24} md={16} className="mb-9">
                                <TextInput
                                    isRequired={true}
                                    isReadonly={!input.isFixedAmount}
                                    prefix={t("txt.rm")}
                                    value={input.fixedAmount}
                                    name="fixedAmount"
                                    onChange={handleFieldChange}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[18, 0]} align='middle'>
                            <Col xs={24} sm={24} md={8} className="mb-9 pr-0">
                                <Checkbox
                                    checked={input.isPercentage}
                                    onChange={e => handleCheckboxChange(e.target.checked, "isPercentage")}
                                >
                                    {t("label.percentage")}
                                </Checkbox>
                            </Col>
                            <Col xs={24} sm={24} md={16} className="mb-0">
                                <TextInput
                                    isRequired={true}
                                    isReadonly={!input.isPercentage}
                                    value={input.percentage}
                                    name="percentage"
                                    suffix={t("txt.percentage")}
                                    onChange={handleFieldChange}
                                />
                            </Col>
                        </Row>
                        {input.isPercentage 
                            ? <Row gutter={[20, 0]} align="middle">
                                <Col xs={24} sm={24} md={8} className="mt-10 mb-0 pr-0">
                                    <label className="ml-24">{t(`label.capAmount`)}</label>
                                </Col>
                                <Col xs={24} sm={24} md={16} className="mt-10 mb-0">
                                    <TextInput
                                        isRequired={true}
                                        prefix={t("txt.rm")}
                                        value={input.capAmount}
                                        name="capAmount"
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </Row>
                            : <></>}
                        <Row>
                            <Col span={24} className="mb-0 alert-box">
                                <AlertBox
                                    text={error["surcharge.fixedAmount"] || error["surcharge.percentage"]
                                        ? t("error.surcharge") 
                                        : error["surcharge.capAmount"]
                                        ? t(`error.pattern`, { field: t(`label.capAmount`) }) 
                                        : null}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className='mt-30'>
                    <Col span={24}>
                        <DateRange
                            isRequired={true}
                            startLabel={t("label.validFrom")}
                            endLabel={t("label.validTo")}
                            value={{
                                startDate: input.validFrom,
                                endDate: input.validTo
                            }}
                            onChange={handleDateChange}
                        />
                        <AlertBox text={error.validFrom ?? error.validTo ?? null} />
                    </Col>
                </Row>
                <Row justify="end">
                    <Col className='mb-0 mt-20'>
                        <Button
                            className='btn-confirm'
                            size="large"
                            type="primary"
                            onClick={handleSubmit}
                            label={t("action.confirm")}
                        />
                        <Button
                            className='btn-cancel'
                            size="large"
                            type="secondary"
                            onClick={onCancel}
                            label={t("action.cancel")}
                        />
                    </Col>
                </Row>
            </form>
        </div>
    );
};

export default CreateEditForm;